import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Plus, FileText, Truck, Receipt, Zap, Eye } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import BatchListRow, { missingDeliveryFields } from '@/components/documents/BatchListRow';
import CreateAndPrintForm from '@/components/documents/CreateAndPrintForm';
import DeliveryPromptDialog from '@/components/documents/DeliveryPromptDialog';
import PrintPreviewModal from '@/components/documents/PrintPreviewModal';
import usePrintBatch from '@/hooks/usePrintBatch';

const PREVIEW_STORAGE_KEY = 'erpz.printPreviewMode';

const DOC_TYPE_INFO = {
  'so': { label: 'Sales Order', icon: FileText, filenamePrefix: 'SO' },
  'delivery-order': { label: 'Surat Jalan', icon: Truck, filenamePrefix: 'SuratJalan' },
  'invoice': { label: 'Invoice', icon: Receipt, filenamePrefix: 'Invoice' },
};

/**
 * Dialog unified untuk cetak SO / Surat Jalan / Invoice per BATCH.
 *
 * Alur:
 *   1. User klik tombol Cetak (SO/SJ/Invoice) di AdminDocuments → dialog terbuka
 *   2. Pre-flight check: batch punya info pengiriman lengkap? Jika tidak → popup isi info
 *   3. Preview mode toggle: jika ON → tampil modal preview; jika OFF → langsung cetak/download
 *   4. Bisa buat batch baru untuk sisa item yang belum dikirim
 *
 * Props:
 *   - tx: transaksi
 *   - docType: 'so' | 'delivery-order' | 'invoice'
 *   - useDotMatrix: boolean
 *   - open, onClose
 */
export default function PrintBatchDialog({ tx, docType, useDotMatrix = false, open, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [deliveryPromptFor, setDeliveryPromptFor] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [previewMode, setPreviewMode] = useState(() => {
    try { return localStorage.getItem(PREVIEW_STORAGE_KEY) === '1'; }
    catch (_) { return false; }
  });

  const info = DOC_TYPE_INFO[docType] || DOC_TYPE_INFO['invoice'];
  const Icon = info.icon;

  const togglePreviewMode = (checked) => {
    setPreviewMode(checked);
    try { localStorage.setItem(PREVIEW_STORAGE_KEY, checked ? '1' : '0'); }
    catch (e) { console.debug('[PrintBatchDialog] localStorage tidak tersedia:', e); }
  };

  const load = useCallback(async () => {
    if (!tx?.id) return;
    setLoading(true);
    try {
      const { data: res } = await api.get(`/transactions/${tx.id}/deliveries`);
      setData(res);
    } catch (e) {
      console.warn('[PrintBatchDialog] gagal load deliveries:', e);
      setData({ batches: [], summary: [], has_remaining: false });
    } finally { setLoading(false); }
  }, [tx?.id]);

  useEffect(() => {
    if (open) {
      setShowCreateForm(false);
      setDeliveryPromptFor(null);
      setPreviewData(null);
      load();
    }
  }, [open, load]);

  // Hook cetak — encapsulate PDF & Dot Matrix logic
  const { doPrintPdf, doPrintDotMatrix, saveDeliveryInfo, printingIdx } = usePrintBatch({
    tx,
    docType,
    previewMode,
    docInfo: info,
    onPreview: setPreviewData,
    onMissingInfo: setDeliveryPromptFor,
  });

  const batches = useMemo(() => data?.batches || [], [data]);
  const summary = useMemo(() => data?.summary || [], [data]);
  const hasRemaining = !!data?.has_remaining;

  /**
   * Alur cetak:
   * - Cek dulu apakah batch punya info pengiriman lengkap
   * - Kalau ada yang kurang → tampilkan popup
   * - Kalau lengkap → panggil printer sesuai mode
   */
  const print = useCallback((batchIndex) => {
    const b = batches.find((x) => x.batch_index === batchIndex);
    if (b) {
      const missing = missingDeliveryFields(b, tx);
      if (missing.length > 0) {
        setDeliveryPromptFor({ batch: b, missing });
        return;
      }
    }
    const runner = useDotMatrix ? doPrintDotMatrix : doPrintPdf;
    runner(batchIndex, batches);
  }, [batches, tx, useDotMatrix, doPrintDotMatrix, doPrintPdf]);

  /** Simpan info pengiriman ke batch lalu langsung cetak */
  const saveDeliveryAndPrint = async (fields) => {
    if (!deliveryPromptFor) return;
    const { batch } = deliveryPromptFor;
    try {
      await saveDeliveryInfo(batch.batch_index, fields);
      toast.success('Info pengiriman disimpan');
      setDeliveryPromptFor(null);
      await load();
      const runner = useDotMatrix ? doPrintDotMatrix : doPrintPdf;
      // Data batches masih stale di closure ini, kita passing batches yg fresh dari load()
      // via callback berikut menggunakan setter fungsi.
      const { data: freshRes } = await api.get(`/transactions/${tx.id}/deliveries`);
      runner(batch.batch_index, freshRes?.batches || []);
    } catch (e) {
      console.error('[PrintBatchDialog] gagal simpan/cetak:', e);
      toast.error(e?.response?.data?.detail || 'Gagal simpan info batch');
    }
  };

  const handleBatchCreated = async (newBatchIdx) => {
    setShowCreateForm(false);
    await load();
    // Refresh batch list dulu, lalu cetak batch baru
    const { data: freshRes } = await api.get(`/transactions/${tx.id}/deliveries`);
    const fresh = freshRes?.batches || [];
    const b = fresh.find((x) => x.batch_index === newBatchIdx);
    if (b && missingDeliveryFields(b, tx).length > 0) {
      setDeliveryPromptFor({ batch: b, missing: missingDeliveryFields(b, tx) });
      return;
    }
    const runner = useDotMatrix ? doPrintDotMatrix : doPrintPdf;
    runner(newBatchIdx, fresh);
  };

  if (!tx) return null;

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid={`print-batch-dialog-${docType}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Icon className="h-4 w-4 text-primary" />
              Cetak {info.label}{' '}
              {useDotMatrix && (
                <Badge variant="outline" className="text-[9px]">
                  <Zap className="h-2.5 w-2.5 mr-0.5" />DOT MATRIX
                </Badge>
              )}
              {' — '}{tx.invoice_no}
            </DialogTitle>
            <div className="flex items-center gap-2 pt-1">
              <Switch
                id="preview-mode-toggle"
                checked={previewMode}
                onCheckedChange={togglePreviewMode}
                data-testid="print-preview-toggle"
              />
              <Label htmlFor="preview-mode-toggle" className="text-xs flex items-center gap-1 cursor-pointer">
                <Eye className="h-3 w-3" /> Pratinjau dulu sebelum cetak
              </Label>
            </div>
          </DialogHeader>

          {loading ? (
            <div className="py-6 text-center text-sm text-muted-foreground">Memuat batch...</div>
          ) : (
            <div className="space-y-3 text-sm">
              {/* Ringkasan pengiriman */}
              {summary.length > 0 && <ShipmentSummary summary={summary} hasRemaining={hasRemaining} />}

              {/* Section A: Batch existing */}
              <BatchListSection
                batches={batches}
                tx={tx}
                docType={docType}
                printingIdx={printingIdx}
                onPrint={print}
              />

              {/* Section B: Buat Batch Baru */}
              {hasRemaining && !showCreateForm && (
                <Button
                  variant="outline"
                  onClick={() => setShowCreateForm(true)}
                  className="w-full border-dashed"
                  data-testid="print-batch-create-btn"
                >
                  <Plus className="h-3.5 w-3.5 mr-1.5" /> Buat Batch Baru untuk Cetak {info.label}
                </Button>
              )}

              {hasRemaining && showCreateForm && (
                <CreateAndPrintForm
                  tx={tx}
                  docType={docType}
                  summary={summary}
                  onCancel={() => setShowCreateForm(false)}
                  onCreated={handleBatchCreated}
                />
              )}

              {!hasRemaining && batches.length > 0 && (
                <div className="rounded-md border border-emerald-300 bg-emerald-50 p-2 text-xs text-emerald-800">
                  ✓ Semua barang sudah tercakup di batch existing. Cetak batch yang diinginkan di atas.
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={onClose}>Tutup</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sub-dialog: minta info pengiriman kalau batch belum lengkap */}
      <Dialog open={!!deliveryPromptFor} onOpenChange={(o) => !o && setDeliveryPromptFor(null)}>
        {deliveryPromptFor && (
          <DeliveryPromptDialog
            batch={deliveryPromptFor.batch}
            tx={tx}
            missing={deliveryPromptFor.missing}
            docLabel={info.label}
            onSave={saveDeliveryAndPrint}
          />
        )}
      </Dialog>

      {/* Modal pratinjau (aktif kalau toggle 'Pratinjau dulu' ON) */}
      <PrintPreviewModal
        open={!!previewData}
        onClose={() => setPreviewData(null)}
        mode={previewData?.mode}
        pdfBlob={previewData?.pdfBlob}
        dmText={previewData?.dmText}
        dmOrientation={previewData?.dmOrientation}
        filename={previewData?.filename}
        title={previewData?.title}
      />
    </>
  );
}

/** Ringkasan progress pengiriman per item. */
function ShipmentSummary({ summary, hasRemaining }) {
  const totalShipped = summary.reduce((a, s) => a + s.quantity_shipped, 0);
  const totalOrder = summary.reduce((a, s) => a + s.quantity_total, 0);
  const totalRemaining = summary.reduce((a, s) => a + s.quantity_remaining, 0);
  return (
    <div className="rounded-md border bg-accent/30 p-2 text-xs" data-testid="print-batch-summary">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-muted-foreground">Progress</span>
        <span className="tabular-nums font-medium">
          {totalShipped} / {totalOrder} btg dikirim
          {hasRemaining && (
            <span className="text-amber-700 ml-2">• {totalRemaining} btg belum dikirim</span>
          )}
        </span>
      </div>
      <div className="space-y-0.5">
        {summary.map((s) => (
          <div key={s.product_id} className="flex justify-between text-[11px]">
            <span className="truncate">{s.name}</span>
            <span className="tabular-nums shrink-0">
              <span className="text-emerald-700">{s.quantity_shipped}</span>
              <span className="text-muted-foreground">/{s.quantity_total}</span>
              {s.quantity_remaining > 0 && (
                <span className="text-amber-700 ml-1.5">(sisa {s.quantity_remaining})</span>
              )}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/** List semua batch existing. */
function BatchListSection({ batches, tx, docType, printingIdx, onPrint }) {
  return (
    <div>
      <div className="text-xs font-semibold mb-1.5">Cetak Batch yang Sudah Ada</div>
      {batches.length === 0 ? (
        <div className="text-xs italic text-muted-foreground p-2 border rounded">
          Belum ada batch (transaksi legacy tanpa split). Buat batch baru di bawah.
        </div>
      ) : (
        <div className="space-y-1.5">
          {batches.map((b) => (
            <BatchListRow
              key={b.batch_index}
              batch={b}
              tx={tx}
              docType={docType}
              printing={printingIdx === b.batch_index}
              onPrint={onPrint}
            />
          ))}
        </div>
      )}
    </div>
  );
}
